import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import Logo from "./Logo";

const NAV = [
    { to: "/shop", label: "Shop" },
    { to: "/categories", label: "Categories" },
    { to: "/best-sellers", label: "Best Sellers" },
    { to: "/blog", label: "Journal" },
    { to: "/about", label: "About" },
];

export default function Navbar() {
    const { count, setOpen } = useCart();
    const [scrolled, setScrolled] = useState(false);
    const [menu, setMenu] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- onScroll captures no external state

    return (
        <>
            {/* Announcement bar */}
            <div
                className="w-full text-center py-2 overline"
                style={{
                    background: "var(--vl-surface)",
                    color: "var(--vl-rose)",
                    borderBottom: "1px solid var(--vl-border)",
                }}
                data-testid="announcement-bar"
            >
                Intimate wellness · 100% discreet shipping · COD available across India
            </div>

            <header
                data-testid="navbar"
                className="sticky top-0 z-40"
                style={{
                    background: scrolled
                        ? "rgba(10,10,11,0.82)"
                        : "rgba(10,10,11,0.55)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    borderBottom: "1px solid var(--vl-border)",
                    transition: "background 260ms ease",
                }}
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setMenu(true)}
                        className="lg:hidden"
                        aria-label="Open menu"
                    >
                        <Menu
                            size={22}
                            strokeWidth={1.25}
                            color="var(--vl-text)"
                        />
                    </button>

                    <nav className="hidden lg:flex items-center gap-10">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                                className={({ isActive }) =>
                                    `text-[12px] tracking-[0.22em] uppercase transition-colors ${isActive ? "text-[var(--vl-rose)]" : "text-[var(--vl-text)] hover:text-[var(--vl-rose)]"}`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <Link
                        to="/"
                        aria-label="Veloura Wellness — home"
                        className="absolute left-1/2 -translate-x-1/2 inline-flex"
                    >
                        {/* Smaller plate on phones so icons breathe; full on ≥sm */}
                        <span className="sm:hidden">
                            <Logo size="xs" variant="plate" />
                        </span>
                        <span className="hidden sm:inline-flex">
                            <Logo size="sm" variant="plate" />
                        </span>
                    </Link>

                    <div className="flex items-center gap-5">
                        <Link
                            to="/shop"
                            data-testid="nav-search"
                            aria-label="Search"
                            className="hidden sm:inline-flex"
                        >
                            <Search
                                size={18}
                                strokeWidth={1.25}
                                color="var(--vl-text)"
                            />
                        </Link>
                        <Link
                            to="/track-order"
                            data-testid="nav-track"
                            aria-label="Track order"
                            className="hidden sm:inline-flex"
                        >
                            <User
                                size={18}
                                strokeWidth={1.25}
                                color="var(--vl-text)"
                            />
                        </Link>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="nav-cart"
                            aria-label="Cart"
                            className="relative"
                        >
                            <ShoppingBag
                                size={18}
                                strokeWidth={1.25}
                                color="var(--vl-text)"
                            />
                            {count > 0 && (
                                <span
                                    className="absolute -top-2 -right-2 text-[10px] w-[18px] h-[18px] flex items-center justify-center"
                                    style={{
                                        background: "var(--vl-rose)",
                                        color: "var(--vl-bg)",
                                        borderRadius: "50%",
                                    }}
                                    data-testid="cart-count"
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile menu */}
            {menu && (
                <div
                    data-testid="mobile-menu"
                    className="fixed inset-0 z-[90] flex"
                    style={{
                        background: "rgba(10,10,11,0.95)",
                        backdropFilter: "blur(16px)",
                    }}
                >
                    <div className="w-full h-full flex flex-col px-8 py-8">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setMenu(false)}
                                data-testid="mobile-menu-close"
                                aria-label="Close"
                            >
                                <X
                                    size={22}
                                    strokeWidth={1.25}
                                    color="var(--vl-text)"
                                />
                            </button>
                        </div>
                        <nav className="mt-16 flex flex-col gap-8">
                            {NAV.map((n) => (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    onClick={() => setMenu(false)}
                                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                                    className="font-serif text-4xl tracking-tight"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    {n.label}
                                </Link>
                            ))}
                            <Link
                                to="/faq"
                                onClick={() => setMenu(false)}
                                data-testid="mobile-nav-faq"
                                className="font-serif text-4xl tracking-tight"
                                style={{ color: "var(--vl-text)" }}
                            >
                                FAQ
                            </Link>
                            <Link
                                to="/contact"
                                onClick={() => setMenu(false)}
                                data-testid="mobile-nav-contact"
                                className="font-serif text-4xl tracking-tight"
                                style={{ color: "var(--vl-text)" }}
                            >
                                Contact
                            </Link>
                        </nav>
                    </div>
                </div>
            )}
        </>
    );
}
